import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import styles from './About.module.css';
import portrait from '../../assets/bilge-portrait.jpeg';

const stats = [
  { value: '15+', label: 'Years of Practice' },
  { value: '4', label: 'Art Disciplines' },
  { value: '3', label: 'Countries Lived' },
  { value: '∞', label: 'Stories to Tell' },
];

export default function About() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.15 });

  const fadeUp = (delay = 0) => ({
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1, y: 0,
      transition: { duration: 0.9, ease: [0.25, 0.46, 0.45, 0.94], delay },
    },
  });

  return (
    <section id="about" className={styles.about} ref={ref}>
      <div className={styles.container}>
        {/* Left: image */}
        <motion.div
          className={styles.imageWrap}
          initial={{ opacity: 0, x: -50 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <div className={styles.imageFrame}>
            <img src={portrait} alt="Bilge — AussieRooArt" className={styles.portrait} />
          </div>
          <div className={styles.imageBadge}>
            <span className={styles.badgeYear}>Est. 2008</span>
            <span className={styles.badgeText}>Fine Arts</span>
          </div>
        </motion.div>

        {/* Right: text */}
        <div className={styles.textCol}>
          <motion.span
            className={styles.sectionLabel}
            variants={fadeUp(0.1)}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
          >
            <span className={styles.labelLine} /> Who is Bilge
          </motion.span>

          <motion.h2
            className={styles.heading}
            variants={fadeUp(0.2)}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
          >
            A Dance Between the Soul on Stage{' '}
            <em>and the Imagination on Canvas</em>
          </motion.h2>

          <motion.p
            className={styles.bio}
            variants={fadeUp(0.32)}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
          >
            Bilge (b. 1994) is a contemporary artist based in Vancouver, Canada. Raised in a family
            that nurtured her creativity, she began exploring art from an early age through theatre,
            painting, and music — later discovering painting and performance as her strongest forms
            of expression.
          </motion.p>

          <motion.p
            className={styles.bio}
            variants={fadeUp(0.42)}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
          >
            At 14, she was accepted into a Fine Arts High School, completing four years of formal
            training in charcoal drawing, composition, oil painting, and sculpture. She later studied
            Fashion &amp; Textile Design, focusing on surface pattern creation inspired by natural
            textures and her surroundings.
          </motion.p>

          <motion.p
            className={styles.bio}
            variants={fadeUp(0.52)}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
          >
            Having lived in Australia, Bilge draws deep inspiration from its landscapes — the raw
            energy of the outback, the warmth of its light, and the calm of coastal environments.
            This connection gave birth to <strong>AussieRooArt</strong>, under which she creates
            textile designs, murals, and interior-focused artistic projects.
          </motion.p>

          <motion.div
            className={styles.stats}
            variants={fadeUp(0.62)}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
          >
            {stats.map((s) => (
              <div className={styles.stat} key={s.label}>
                <span className={styles.statValue}>{s.value}</span>
                <span className={styles.statLabel}>{s.label}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
